import { getLocalizedString } from "common/components/localization";
import { getAppKindFromFeatures } from "common/components/SerializedApp";
import { isRunningInNativeIos } from "common/native-hooks";
import { defined, isEmptyOrUndefined, isValidEmailAddress } from "common/Support";
import { getStringProperty } from "generator/components/property-utils";
import * as React from "react";
import "twin.macro";
import { getUrlHandling, UrlHandling } from "webapp/lib/link-utils";
import type { WireAppTheme } from "webapp/lib/theme/theme";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { WireCheckbox } from "webapp/ui/views/wire-checkbox/wire-checkbox";
import { WireField } from "webapp/ui/views/wire-field/wire-field";
import { UIButtonAppearance } from "wire/types";
import type { BaseProps } from "../custom-sign-in-props";
import { ExternalAuthProviderButtons } from "../google-sso";

export type PagesLoginSource = "main-sign-in" | "modal-sign-in" | "modal-sign-up";

export function getDefaultPagesGreeting(
    pagesSource: PagesLoginSource | undefined,
    appTitle: string | undefined
): string {
    const postFix = appTitle !== undefined ? ` to ${appTitle}` : "";
    if (pagesSource === "modal-sign-up") {
        // TODO: Localize this
        return `Sign up${postFix}`;
    }
    // TODO: Localize this
    return `Log in${postFix}`;
}

interface Props extends BaseProps {
    readonly theme: WireAppTheme;
    readonly pagesSource?: PagesLoginSource;
    readonly onPageSourceChanged?: (newPageSource: PagesLoginSource) => void;
}

export const WireEmailInput: React.VFC<Props> = p => {
    const {
        currentState,
        greeting,
        description,
        userEmail,
        pin,
        canContinue,
        canPressPin,
        error,
        isSending,
        theme,
        userAgreed,
        appFeatures,
        authMethod,
        pagesSource,
        appTitle,
        allowSaveLogin,
        onPressedContinue,
        onEmailChange,
        onPinChange,
        onPressedPin,
        onUserAgreedChange,
        onPageSourceChanged,
        onUserUpdateSaveLogin,
    } = p;

    const onFormSubmit = React.useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();

            const userAgreementRequired = theme.requiresAgreement === true;
            const userAgreedValidated = !userAgreementRequired || (userAgreementRequired && userAgreed);

            if (onPressedContinue && userAgreedValidated) onPressedContinue();
        },
        [onPressedContinue, theme.requiresAgreement, userAgreed]
    );

    // REVIEW: Why do we pull from features here?
    const appKind = getAppKindFromFeatures(appFeatures);
    const { requiresAgreement, agreementText: agreementTextProperty, policyLinks } = theme;
    const agreementText = getStringProperty(agreementTextProperty);
    const agreementTextOrDefault = isEmptyOrUndefined(agreementText)
        ? getLocalizedString("userAgreement", appKind)
        : agreementText;

    const userAgreementRequired = requiresAgreement === true;
    const userAgreedValidated = !userAgreementRequired || (userAgreementRequired && userAgreed);

    const canProgressToPin = canContinue === true && isValidEmailAddress(userEmail) && userAgreedValidated;

    const isGreetingDefault = getDefaultPagesGreeting(pagesSource, appTitle) === greeting;

    let topContent: React.ReactNode;
    let buttons: React.ReactNode;
    if (currentState === "challenge") {
        topContent = (
            <div>
                <div
                    tw="text-2xl font-extrabold text-text-dark mb-5"
                    data-test="app-sign-in-title"
                    // FIXME: Localize this
                >
                    Check your email
                </div>
                <form action="" onSubmit={onFormSubmit}>
                    <WireField
                        data-test="app-pin-input"
                        // FIXME: Localize this
                        title={`We've sent a PIN to ${userEmail}`}
                        isEnabled={!isSending}
                        placeholder="00000"
                        value={pin}
                        onChange={onPinChange}
                        dataType="tel"
                        dataTypePattern="[0-9]*"
                        error={error}
                        autoFocus={true}
                    />
                </form>
            </div>
        );

        buttons = (
            <div>
                <WireButton
                    onClick={onPressedContinue}
                    disabled={canContinue === false}
                    appearance={UIButtonAppearance.Filled}
                    size={"md"}
                    tw="w-full h-10 mt-5">
                    {getLocalizedString("signIn", appKind)}
                </WireButton>
                <WireButton
                    onClick={onPressedPin}
                    disabled={canPressPin === false}
                    appearance={UIButtonAppearance.Simple}
                    size={"md"}
                    tw="w-full h-10 mt-5 text-accent"
                    // FIXME: Localize this
                >
                    I need another PIN
                </WireButton>
            </div>
        );
    } else {
        const policyLinksNode =
            policyLinks === undefined || policyLinks.length === 0 ? null : (
                <>
                    {policyLinks.map((pl, i) => {
                        const title = getStringProperty(pl.title);
                        const href = getStringProperty(pl.url);
                        const handling = getUrlHandling(defined(href), false);
                        const useTag = handling === UrlHandling.UseATag || handling === UrlHandling.UseATagWithBlank;
                        const target = handling === UrlHandling.UseATagWithBlank ? "_blank" : undefined;

                        if (isEmptyOrUndefined(title) || isEmptyOrUndefined(href)) return <React.Fragment key={i} />;

                        return (
                            // NOTE: Only prefix with commas for links if it not the first element
                            <>
                                {i !== 0 ? <span>, </span> : null}
                                <a
                                    tw="leading-5 border-b border-n700"
                                    href={useTag ? href : undefined}
                                    target={target}
                                    key={i}>
                                    {title}
                                </a>
                            </>
                        );
                    })}
                </>
            );

        const agreementLabel = (
            <div>
                <span tw="leading-5 text-n700">{agreementTextOrDefault}</span>
                {policyLinksNode}
            </div>
        );

        const authCookieLabel = (
            <div>
                <div>{getLocalizedString("saveLoginPrimay", appKind)}</div>
                <div tw="text-xs text-text-xpale">{getLocalizedString("saveLoginSecondary", appKind)}</div>
            </div>
        );

        topContent = (
            <div>
                <div tw="text-2xl font-extrabold mb-5 text-text-dark" data-test="app-sign-in-title">
                    {greeting}
                </div>
                <form action="" onSubmit={onFormSubmit}>
                    <WireField
                        title={description ?? undefined}
                        data-test="app-email-input"
                        isEnabled={!isSending}
                        // FIXME: Localize this
                        placeholder="Enter your email"
                        value={userEmail}
                        onChange={onEmailChange}
                        dataType="email"
                        error={error}
                        autoFocus={true}
                    />
                </form>
                {userAgreementRequired && (
                    <div tw="mt-5">
                        <WireCheckbox label={agreementLabel} checked={userAgreed} onCheck={onUserAgreedChange} />
                    </div>
                )}
                {appFeatures.askUserToSaveAuthCookie && (
                    <div tw="mt-4">
                        <WireCheckbox
                            label={authCookieLabel}
                            checked={allowSaveLogin}
                            onCheck={onUserUpdateSaveLogin}
                        />
                    </div>
                )}
            </div>
        );

        let buttonText = getLocalizedString("continue", appKind);
        if (isSending) {
            buttonText = getLocalizedString("sendingPin", appKind);
        } else if (!isGreetingDefault) {
            buttonText = pagesSource === "modal-sign-up" ? "Sign Up" : "Sign In";
        }

        buttons = (
            <div>
                <WireButton
                    onClick={onPressedContinue}
                    disabled={!canProgressToPin}
                    appearance={UIButtonAppearance.Filled}
                    size={"md"}
                    tw="w-full h-10 mt-5">
                    {buttonText}
                </WireButton>

                {!isRunningInNativeIos() && (
                    <ExternalAuthProviderButtons
                        authMethod={authMethod}
                        {...p}
                        userAgreed={userAgreedValidated}
                        appKind={appKind}
                    />
                )}
            </div>
        );
    }

    const showSignUpFooter = pagesSource === "modal-sign-in" || pagesSource === "modal-sign-up";
    const descriptionText = pagesSource === "modal-sign-in" ? "Don't have an account yet?" : "Already have an account?";
    const actionText = pagesSource === "modal-sign-in" ? "Sign up" : "Customer log in";
    const action =
        pagesSource === "modal-sign-in"
            ? () => onPageSourceChanged?.("modal-sign-up")
            : () => onPageSourceChanged?.("modal-sign-in");

    return (
        <>
            {topContent}
            {buttons}
            {showSignUpFooter && (
                <div tw="flex items-center justify-center text-sm text-n600 mt-6">
                    <span>{descriptionText}</span>
                    <span tw="text-accent font-medium pl-1 cursor-pointer" onClick={action}>
                        {actionText}
                    </span>
                </div>
            )}
        </>
    );
};
